import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { governanceService } from "@/services/governance.service";

export default function PlatformAuditPage() {
  const [actorEmail, setActorEmail] = useState("");
  const [action, setAction] = useState("");
  const [outcome, setOutcome] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [page, setPage] = useState(1);
  const params = useMemo(
    () => ({
      page,
      limit: 25,
      actorEmail: actorEmail || undefined,
      action: action || undefined,
      outcome: outcome || undefined,
      tenantId: tenantId || undefined,
    }),
    [action, actorEmail, outcome, page, tenantId],
  );
  const { data, isLoading } = useQuery({
    queryKey: ["platform-audit-events", params],
    queryFn: () => governanceService.listPlatformAuditEvents(params),
  });

  const resetPage = (setter: (value: string) => void, value: string) => {
    setter(value);
    setPage(1);
  };

  return (
    <div className="space-y-5">
      <div className="grid gap-3 lg:grid-cols-4">
        <input
          value={actorEmail}
          onChange={(event) => resetPage(setActorEmail, event.target.value)}
          placeholder="Actor email"
          className="h-10 rounded-xl border border-slate-200 px-3 text-sm"
        />
        <input
          value={action}
          onChange={(event) => resetPage(setAction, event.target.value)}
          placeholder="Action"
          className="h-10 rounded-xl border border-slate-200 px-3 text-sm"
        />
        <select
          value={outcome}
          onChange={(event) => resetPage(setOutcome, event.target.value)}
          className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm"
        >
          <option value="">All outcomes</option>
          <option value="SUCCEEDED">Succeeded</option>
          <option value="FAILED">Failed</option>
        </select>
        <input
          value={tenantId}
          onChange={(event) => resetPage(setTenantId, event.target.value)}
          placeholder="Tenant ID"
          className="h-10 rounded-xl border border-slate-200 px-3 text-sm"
        />
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full min-w-[900px] text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-[0.16em] text-slate-400">
            <tr>
              <th className="px-4 py-3">Time</th>
              <th className="px-4 py-3">Actor</th>
              <th className="px-4 py-3">Action</th>
              <th className="px-4 py-3">Resource</th>
              <th className="px-4 py-3">Tenant</th>
              <th className="px-4 py-3">Outcome</th>
              <th className="px-4 py-3">Reason / error</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-slate-500">
                  Loading platform audit events…
                </td>
              </tr>
            ) : data?.data.length ? (
              data.data.map((event) => (
                <tr
                  key={event.id}
                  className="border-t border-slate-100 align-top"
                >
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {new Date(event.occurredAt).toLocaleString()}
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-slate-800">
                      {event.actorEmail || "System"}
                    </p>
                    <p className="text-xs text-slate-400">{event.actorRole}</p>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-700">
                    {event.action}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {event.resourceType || "—"}
                    <br />
                    {event.resourceId || ""}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">
                    {event.tenantId || "—"}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={
                        event.outcome === "SUCCEEDED"
                          ? "rounded-full bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700"
                          : "rounded-full bg-red-50 px-2 py-1 text-xs font-semibold text-red-700"
                      }
                    >
                      {event.outcome}
                    </span>
                  </td>
                  <td className="max-w-xs px-4 py-3 text-xs text-slate-500">
                    {event.reason ||
                      event.errorMessage ||
                      event.errorCode ||
                      "—"}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-slate-500">
                  No platform audit events found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-slate-500">
        <span>
          Page {data?.meta.page ?? page} of {data?.meta.totalPages ?? 1}
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((value) => Math.max(1, value - 1))}
            className="rounded-lg border border-slate-200 px-3 py-1 disabled:opacity-50"
          >
            Previous
          </button>
          <button
            type="button"
            disabled={Boolean(data) && page >= (data?.meta.totalPages ?? 1)}
            onClick={() => setPage((value) => value + 1)}
            className="rounded-lg border border-slate-200 px-3 py-1 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
